import React, { useCallback, useContext, useMemo } from 'react';
import { TranslatorContext } from '@jutro/locale';
import _ from 'lodash';
import { Button, RadioButtonField } from '@jutro/components';
import { SgCardService } from 'sg-capability-card';
import CommonConstants from 'sg-common-constants';
import messages from './SgCreditCardDetails.messages';

function SgCreditCardDetails(props) {
    const {
        id,
        paymentDetails,
        paymentInstrumentVM,
        setPaymentInstrumentVM,
        setEditAutoRenewal,
        setShowChangeDetailsForm,
        editAutoRenewal,
        isInRenewalCycle,
        isRenewable,
        productCode
    } = props;

    const translator = useContext(TranslatorContext);

    const displayAutoRenewal = useMemo(() => {
        if (productCode === CommonConstants.PRIVATE_MEDICAL_INSURANCE_CODE) {
            return false;
        }
        return true;
    }, [productCode]);

    const autoRenewalDisplay = useMemo(() => {
        if (_.isNil(paymentInstrumentVM) || _.isNil(paymentInstrumentVM.value.creditCardData)) return null;
        return (
            <div>
                <span className="sgCepPaymentsAutoRenewalLabel">
                    {paymentInstrumentVM.value.creditCardData.autoRenewal_Ext ? translator(messages.Yes) : translator(messages.No)}
                </span>
                {displayAutoRenewal && setShowChangeDetailsForm && !isInRenewalCycle
                    && (
                        <Button
                            id={`amendAutoRenewalButton_${id}`}
                            onClick={() => setShowChangeDetailsForm(true)} // SD-1407 Amend button now behaves as Change payment details
                        >
                            {translator(messages.Amend)}
                        </Button>
                    )
                }
            </div>
        );
    }, [id, paymentInstrumentVM, setEditAutoRenewal, translator]);

    const handleSaveAutoRenewal = useCallback(() => {
        SgCardService.manageBCPaymentInstrument(paymentDetails.displayName, paymentInstrumentVM.value.creditCardData.token,
            paymentInstrumentVM.value.creditCardData.autoRenewal_Ext, paymentInstrumentVM.value.publicID)
            .then(() => {
                setEditAutoRenewal(false);
            });
    }, [paymentDetails, paymentInstrumentVM, setEditAutoRenewal]);

    const updateAutoRenewal = useCallback((value) => {
        const newPaymentInstrumentVM = _.cloneDeep(paymentInstrumentVM);
        _.set(newPaymentInstrumentVM, 'value.creditCardData.autoRenewal_Ext', value);
        setPaymentInstrumentVM(newPaymentInstrumentVM);
    }, [paymentInstrumentVM, setPaymentInstrumentVM]);

    const autoRenewalForm = useMemo(() => {
        if (_.isNil(paymentInstrumentVM) || _.isNil(paymentInstrumentVM.value.creditCardData)) return null;
        return (
            <div className="sgCepPaymentsAutoRenewalToggleContainer">
                <RadioButtonField
                    id={`autoRenewal_${id}`}
                    controlClassName="sgBooleanRadioButtons"
                    showValidationIcon={false}
                    value={paymentInstrumentVM.value.creditCardData.autoRenewal_Ext}
                    onValueChange={(value) => updateAutoRenewal(value)}
                />
                <Button
                    id={`saveAutoRenewalButton_${id}`}
                    onClick={handleSaveAutoRenewal}
                    className="sgButton sgSecondaryButton"
                >
                    {translator(messages.Save)}
                </Button>
            </div>
        );
    }, [handleSaveAutoRenewal, id, paymentInstrumentVM, setPaymentInstrumentVM, translator]);

    const autoRenewalEditComponent = useMemo(() => {
        if (_.isNil(paymentDetails) || _.isNil(paymentDetails.paymentInstrument.creditCardData)) return null;
        return (
            <div>
                {!editAutoRenewal && autoRenewalDisplay}
                {editAutoRenewal && autoRenewalForm}
            </div>
        );
    }, [autoRenewalDisplay, autoRenewalForm, editAutoRenewal, paymentDetails]);

    return (
        <div id={`creditCardDetails_${id}`}>
            <div id={`cardHolderName_${id}`} className="sgPolicyInfo">
                <div className="sgPolicyInfoName">{translator(messages.CardHolderName)}</div>
                <div className="sgPolicyInfoValue">{paymentInstrumentVM.value.creditCardData.creditCardHolder_Ext}</div>
            </div>
            <div id={`cardNumber_${id}`} className="sgPolicyInfo">
                <div className="sgPolicyInfoName">{translator(messages.CardNumber)}</div>
                <div className="sgPolicyInfoValue">{paymentInstrumentVM.value.creditCardData.creditCardNumber}</div>
            </div>
            <div id={`expiryDate_${id}`} className="sgPolicyInfo">
                <div className="sgPolicyInfoName">{translator(messages.ExpiryDate)}</div>
                <div className="sgPolicyInfoValue">{paymentInstrumentVM.value.creditCardData.creditCardExpDate}</div>
            </div>
            { isRenewable && (
                <div id={`autoRenewal_${id}`} className="sgPolicyInfo">
                    <div className="sgPolicyInfoName">{translator(messages.UsedForAutoRenewal)}</div>
                    <div className="sgPolicyInfoValue">{autoRenewalEditComponent}</div>
                </div>
            )}
        </div>
    );
}

export default SgCreditCardDetails;
